import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const YOUTUBE_ID = "vnGyaqWrptg";

const SLIDES = [
  { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/71txfb79_Le%20pont%20neuf%20-%20TOULOUSE_MIDI_TOULOUSAIN%20-%20Copie.jpg", caption: { fr: "Toulouse · Le Pont Neuf sur la Garonne", en: "Toulouse · The Pont Neuf over the Garonne" } },
  { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/yh3e8sxn_pont-deyme-vnf.jpg", caption: { fr: "Pont de Deyme · l'arche de brique se reflétant dans l'eau", en: "Deyme bridge · the brick arch reflected in the water" } },
  { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/166086f2_1000550422.webp", caption: { fr: "Bassin de Saint-Ferréol · le réservoir de Riquet", en: "Saint-Ferréol basin · Riquet's reservoir" } },
  { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/vixxxr17_1000550458.webp", caption: { fr: "Saint-Ferréol · la digue et le chemin de halage", en: "Saint-Ferréol · the dam and the towpath" } },
  { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/413xxa9t_le%20malpas%202.webp", caption: { fr: "Le Malpas · vu de l'intérieur, la prouesse du XVIIᵉ siècle", en: "Le Malpas · seen from inside, the 17th-century feat" } },
];

const numbers = [
  { n: "240", u: "km", l: { fr: "Le canal, de Toulouse à Sète", en: "The canal, from Toulouse to Sète" } },
  { n: "14", u: { fr: "ans", en: "yrs" }, l: { fr: "De travaux, 1666 → 1681", en: "Of construction, 1666 → 1681" } },
  { n: "12 000", u: "", l: { fr: "Ouvriers, dont de nombreuses femmes", en: "Workers, including many women" } },
  { n: "1996", u: "", l: { fr: "Patrimoine mondial UNESCO", en: "UNESCO World Heritage" } },
];

const timeline = [
  { y: "1604", t: { fr: "Naissance de Riquet", en: "Riquet is born" }, d: { fr: "Pierre-Paul Riquet naît à Béziers. Rien ne le prédestine à devenir ingénieur.", en: "Pierre-Paul Riquet is born in Béziers. Nothing destines him to become an engineer." } },
  { y: "1640", t: { fr: "L'idée germe", en: "The idea takes root" }, d: { fr: "Il observe les sources et ruisseaux de la Montagne Noire — comment capter l'eau pour alimenter un canal au point le plus haut ?", en: "He observes the springs and streams of the Montagne Noire — how to capture water to feed a canal at its highest point?" } },
  { y: "1662", t: { fr: "La solution trouvée", en: "The solution found" }, d: { fr: "Riquet résout l'énigme du partage des eaux : un réseau de rigoles captant les eaux de la Montagne Noire vers le seuil de Naurouze.", en: "Riquet solves the watershed puzzle: a network of channels carrying the waters of the Montagne Noire to the Naurouze threshold." } },
  { y: "1666", t: { fr: "L'Édit Royal", en: "The Royal Edict" }, d: { fr: "Louis XIV signe l'édit autorisant la construction du Canal Royal de Languedoc. Les travaux commencent.", en: "Louis XIV signs the edict authorising the construction of the Canal Royal de Languedoc. Work begins." } },
  { y: "1672", t: { fr: "Le Barrage de Saint-Ferréol", en: "The Saint-Ferréol Dam" }, d: { fr: "Construction du plus grand barrage d'Europe à l'époque — chef-d'œuvre hydraulique qui alimente le canal.", en: "Construction of the largest dam in Europe at the time — a hydraulic masterpiece that feeds the canal." } },
  { y: "1681", t: { fr: "Inauguration", en: "Inauguration" }, d: { fr: "Le canal est inauguré. Riquet meurt six mois avant, sans voir son rêve accompli. Il mourra endetté.", en: "The canal is inaugurated. Riquet dies six months earlier, never seeing his dream fulfilled. He dies in debt." } },
];

export default function RiquetSection() {
  const { lang } = useLang();
  const en = lang === "en";
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 4500);
    return () => clearInterval(iv);
  }, []);

  return (
    <section className="relative py-32 md:py-40 px-6 md:px-12 bg-bg overflow-hidden" data-testid="riquet-section">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "The origins · UNESCO Heritage 1996 · 30th anniversary 2026" : "Aux origines · Patrimoine UNESCO 1996 · 30e anniversaire 2026"}</div>
            <h2 className="font-serif font-light text-6xl md:text-8xl leading-[0.9] tracking-tight mt-6">
              Pierre-Paul <em className="italic">Riquet</em><br/>& le Canal du Midi.
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg md:text-xl font-serif italic">
              {en
                ? "The incredible story of a 60-year-old tax collector who dared to defy the laws of nature to link the Atlantic to the Mediterranean — achieving what Leonardo da Vinci could not."
                : "L'histoire incroyable d'un collecteur d'impôts de 60 ans qui osa défier les lois de la nature pour relier l'Atlantique à la Méditerranée — réalisant ce que Léonard de Vinci n'avait pu accomplir."}
            </p>
          </div>
        </div>

        {/* Video CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="relative overflow-hidden border border-fg/20 mb-24"
          data-testid="riquet-video"
        >
          <div className="relative aspect-[21/9] overflow-hidden bg-fg">
            <AnimatePresence mode="sync">
              <motion.img
                key={slide}
                src={SLIDES[slide].url}
                alt={SLIDES[slide].caption[lang]}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1.02 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 1.4 }, scale: { duration: 6, ease: "linear" } }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-fg/45" />

            <a
              href={`https://www.youtube.com/watch?v=${YOUTUBE_ID}`}
              target="_blank"
              rel="noreferrer"
              className="absolute inset-0 flex items-center justify-center text-bg w-full h-full group"
              aria-label="Regarder le documentaire sur Riquet sur YouTube"
              data-testid="riquet-play"
            >
              <div className="text-center px-6">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-bg/80 flex items-center justify-center mx-auto group-hover:bg-cinabre group-hover:border-cinabre transition-colors duration-500">
                  <Play size={28} className="ml-1" />
                </div>
                <div className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase">{en ? "Watch the epic on video" : "Découvrez l'épopée en vidéo"}</div>
                <div className="mt-2 font-serif italic text-2xl md:text-3xl">{en ? "The documentary tracing the full story of the Canal du Midi" : "Le documentaire qui retrace l'histoire complète du Canal du Midi"}</div>
                <div className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-bg/70 inline-flex items-center gap-2">
                  {en ? "Watch on YouTube" : "Regarder sur YouTube"} <ExternalLink size={12} />
                </div>
              </div>
            </a>

            <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between gap-4 pointer-events-none">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-bg/85 max-w-[75%]">
                {SLIDES[slide].caption[lang]}
              </div>
              <div className="flex gap-1.5">
                {SLIDES.map((_, i) => (
                  <span key={i} className={`h-[2px] transition-all duration-500 ${i === slide ? "w-8 bg-cinabre" : "w-4 bg-bg/40"}`} />
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-fg/15 bg-bg flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              {en ? "Documentary · Pierre-Paul Riquet & the Canal du Midi" : "Documentaire · Pierre-Paul Riquet & le Canal du Midi"}
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${YOUTUBE_ID}`}
              target="_blank"
              rel="noreferrer"
              className="link-underline font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre inline-flex items-center gap-2"
              data-testid="riquet-youtube-link"
            >
              {en ? "Open on YouTube" : "Ouvrir sur YouTube"} <ExternalLink size={12} />
            </a>
          </div>
        </motion.div>

        {/* Numbers */}
        <div className="mb-24">
          <div className="meta-label text-fg/60 mb-8">— {en ? "The canal in numbers" : "Le canal en chiffres"}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-fg/20 border border-fg/20">
            {numbers.map((n, i) => (
              <motion.div
                key={n.l.fr}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                className="bg-bg p-6 md:p-8"
                data-testid={`riquet-stat-${i}`}
              >
                <div className="font-serif font-light text-5xl md:text-6xl leading-none tracking-tight">
                  {n.n}<span className="text-cinabre font-mono text-lg align-top ml-1">{typeof n.u === "string" ? n.u : n.u[lang]}</span>
                </div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 mt-4">{n.l[lang]}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Story */}
        <div className="grid md:grid-cols-12 gap-12 mb-24">
          <div className="md:col-span-5">
            <div className="meta-label text-cinabre">§ {en ? "The impossible challenge" : "L'impossible défi"}</div>
            <h3 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05] tracking-tight">
              {en ? "What the Romans, da Vinci and the Renaissance had failed to solve…" : "Ce que les Romains, Vinci et la Renaissance n'avaient pas réussi à résoudre…"}
            </h3>
          </div>
          <div className="md:col-span-7 space-y-6 text-fg/85 leading-relaxed text-lg">
            {en ? (
              <>
                <p>In the 17th century, the idea of directly linking the Atlantic Ocean and the Mediterranean Sea across France was considered technical suicide. The Romans, Leonardo da Vinci and the greatest Renaissance engineers had all run into the same puzzle: how do you fill a canal at the top of a watershed, where rivers refuse to flow uphill?</p>
                <p>It was a 60-year-old tax collector, with no engineering training whatsoever, who would solve this age-old mystery. Pierre-Paul Riquet spent years pacing the Montagne Noire, observing springs and channels, understanding how rainwater flows naturally. He designed a network of collection channels to carry water to the canal's highest point, the threshold of <em className="font-serif italic">Naurouze</em>.</p>
                <p>In 1666, as France suffocated under Spanish tolls at the Strait of Gibraltar, Riquet submitted his plan to Colbert, Louis XIV's all-powerful minister. The king signed the edict: the "<em className="font-serif italic">Canal Royal de Languedoc</em>" would be built. Riquet staked his personal fortune on the venture — he would die in debt, six months before the inauguration of his masterpiece.</p>
              </>
            ) : (
              <>
                <p>Au XVIIᵉ siècle, l'idée de relier l'Océan Atlantique et la Mer Méditerranée directement à travers la France était considérée comme un suicide technique. Les Romains, Léonard de Vinci et les plus grands ingénieurs de la Renaissance s'étaient tous heurtés à la même énigme : comment remplir un canal au sommet d'un partage des eaux, là où les rivières refusent de couler vers le haut ?</p>
                <p>C'est un collecteur d'impôts de 60 ans, sans aucune formation d'ingénieur, qui allait résoudre ce mystère millénaire. Pierre-Paul Riquet passe des années à arpenter la Montagne Noire, à observer les sources et les rigoles, à comprendre comment les eaux de pluie s'écoulent naturellement. Il conçoit un réseau de rigoles de collecte pour acheminer l'eau jusqu'au point culminant du canal, le seuil de <em className="font-serif italic">Naurouze</em>.</p>
                <p>En 1666, alors que la France étouffe sous les taxes espagnoles au détroit de Gibraltar, Riquet soumet son plan à Colbert, le tout-puissant ministre de Louis XIV. Le roi signe l'édit : le « <em className="font-serif italic">Canal Royal de Languedoc</em> » sera construit. Riquet engage sa fortune personnelle dans l'aventure — il mourra endetté, six mois avant l'inauguration de son chef-d'œuvre.</p>
              </>
            )}
          </div>
        </div>

        {/* Chronologie */}
        <div>
          <div className="meta-label text-fg/60 mb-8">— {en ? "Timeline" : "Chronologie"}</div>
          <div className="grid md:grid-cols-2 gap-4">
            {timeline.map((t, i) => (
              <motion.div
                key={t.y}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.05 }}
                className="border border-fg/15 p-6 md:p-8 group hover:bg-fg hover:text-bg transition-colors duration-500"
                data-testid={`riquet-timeline-${t.y}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-5xl md:text-6xl leading-none tracking-tight">{t.y}</span>
                  <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">§ 0{i+1}</span>
                </div>
                <div className="font-serif text-2xl md:text-3xl mt-6 leading-tight tracking-tight">{t.t[lang]}</div>
                <p className="mt-4 leading-relaxed opacity-80">{t.d[lang]}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* L'ingénierie · Naurouze & Saint-Ferréol */}
        <div className="mt-24 md:mt-32" data-testid="riquet-hydraulique">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-7">
              <div className="meta-label text-cinabre">§ {en ? "Riquet's engineering" : "L'ingénierie de Riquet"}</div>
              <h3 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
                Naurouze<br/>& <em className="italic">Saint-Ferréol</em>.
              </h3>
            </div>
            <div className="md:col-span-5 self-end">
              <p className="text-fg/80 leading-relaxed text-lg">
                {en ? "Two inseparable elements of the Canal du Midi's hydraulic system. One stores the water; the other distributes it between the two slopes — Atlantic and Mediterranean. Without this duo, the canal could never have been supplied." : "Deux éléments indissociables du système hydraulique du Canal du Midi. L'un stocke l'eau ; l'autre la répartit entre les deux versants — Atlantique et Méditerranée. Sans ce duo, le canal n'aurait jamais pu être alimenté."}
              </p>
            </div>
          </div>

          {/* Diagramme simplifié */}
          <div className="border border-fg/15 p-8 md:p-12 bg-bg mb-12">
            <div className="grid md:grid-cols-12 items-center gap-6 md:gap-4 font-mono text-[10px] tracking-[0.24em] uppercase">
              <div className="md:col-span-3 text-center">
                <div className="font-serif text-3xl md:text-4xl normal-case tracking-tight">Montagne Noire</div>
                <div className="mt-2 text-fg/60">{en ? "Springs · streams" : "Sources · ruisseaux"}</div>
              </div>
              <div className="md:col-span-1 text-center text-cinabre">— ↓ —</div>
              <div className="md:col-span-3 text-center border-x md:border-y-0 border-y border-fg/15 py-6">
                <div className="font-serif text-3xl md:text-4xl normal-case tracking-tight">Saint-Ferréol</div>
                <div className="mt-2 text-fg/60">{en ? "Reservoir · 1672" : "Réservoir · 1672"}</div>
              </div>
              <div className="md:col-span-1 text-center text-cinabre">— ↓ —</div>
              <div className="md:col-span-4 text-center">
                <div className="font-serif text-3xl md:text-4xl normal-case tracking-tight">Seuil de Naurouze</div>
                <div className="mt-2 text-fg/60">{en ? "Highest point · 194 m · summit reach" : "Point culminant · 194 m · bief de partage"}</div>
              </div>
            </div>
            <div className="mt-8 grid md:grid-cols-2 gap-6 text-center font-mono text-[10px] tracking-[0.24em] uppercase">
              <div className="border-t border-fg/20 pt-6">
                <div className="text-cinabre">{en ? "← Atlantic Ocean" : "← Océan Atlantique"}</div>
                <div className="mt-2 font-serif italic text-xl text-fg normal-case tracking-tight">{en ? "towards Toulouse" : "vers Toulouse"}</div>
              </div>
              <div className="border-t border-fg/20 pt-6">
                <div className="text-cinabre">{en ? "Mediterranean Sea →" : "Mer Méditerranée →"}</div>
                <div className="mt-2 font-serif italic text-xl text-fg normal-case tracking-tight">{en ? "towards Sète / Étang de Thau" : "vers Sète / Étang de Thau"}</div>
              </div>
            </div>
          </div>

          {/* Deux blocs texte : Naurouze & Saint-Ferréol */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <motion.article
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="border border-fg/15 p-8 md:p-10"
            >
              <div className="meta-label text-cinabre">§ {en ? "The highest point" : "Le point culminant"}</div>
              <h4 className="font-serif text-3xl md:text-4xl mt-4 leading-tight tracking-tight">
                Seuil de Naurouze
              </h4>
              <p className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{en ? "Montferrand · 189–194 m altitude" : "Montferrand · 189–194 m d'altitude"}</p>
              <p className="mt-6 text-fg/85 leading-relaxed">
                {en ? <>The highest point of the canal — the <em>watershed line</em> between the Atlantic basin (towards Toulouse) and the Mediterranean basin (towards Carcassonne). Water must arrive here in sufficient quantity to flow naturally back down towards the two seas.</> : <>Le point le plus élevé du canal — la <em>ligne de partage des eaux</em> entre le bassin Atlantique (vers Toulouse) et le bassin Méditerranéen (vers Carcassonne). L'eau doit y arriver en quantité suffisante pour redescendre naturellement vers les deux mers.</>}
              </p>
              <p className="mt-4 text-fg/85 leading-relaxed">
                {en ? <>Riquet designed a 5 km <em>summit reach</em> here between the Ocean lock and the Mediterranean lock. The site once held a monumental basin (≈ 400 × 300 m), now partly filled in.</> : <>Riquet y a conçu un <em>bief de partage</em> de 5 km entre l'écluse de l'Océan et l'écluse de la Méditerranée. Le site accueillait autrefois un grand bassin monumental (≈ 400 × 300 m), aujourd'hui en partie comblé.</>}
              </p>
              <p className="mt-4 text-fg/85 leading-relaxed">
                {en ? <>A site already known in Antiquity — Strabo called it <em>the Gallic isthmus</em>. It was by observing a local spring that Riquet reportedly understood that water could naturally split in two directions — the founding idea of the canal.</> : <>Site déjà connu dans l'Antiquité — Strabon l'appelait <em>l'isthme gaulois</em>. C'est en observant une source locale que Riquet aurait compris que l'eau pouvait se séparer naturellement en deux directions — idée fondatrice du canal.</>}
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.08 }}
              className="border border-fg/15 p-8 md:p-10"
            >
              <div className="meta-label text-cinabre">§ {en ? "The reservoir" : "Le réservoir"}</div>
              <h4 className="font-serif text-3xl md:text-4xl mt-4 leading-tight tracking-tight">
                Bassin de Saint-Ferréol
              </h4>
              <p className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">Montagne Noire · 1667 → 1672</p>
              <p className="mt-6 text-fg/85 leading-relaxed">
                {en ? <>An artificial reservoir built between 1667 and 1672. At the time, the <em>largest dam in Europe</em>. An 800 m long, 30 m high dam holds back the water captured by the <em>rigole de la Montagne</em>, which collects the streams of the Montagne Noire.</> : <>Réservoir artificiel construit entre 1667 et 1672. À l'époque, le <em>plus grand barrage d'Europe</em>. Une digue de 800 m de long et 30 m de haut retient l'eau captée par la <em>rigole de la Montagne</em>, qui recueille les ruisseaux de la Montagne Noire.</>}
              </p>
              <p className="mt-4 text-fg/85 leading-relaxed">
                {en ? <>The water flows out through the <em>rigole de la Plaine</em>, roughly 40 km long, down to the Naurouze threshold. The basin guarantees a constant flow for the canal, even in times of drought.</> : <>L'eau repart par la <em>rigole de la Plaine</em>, longue d'environ 40 km, jusqu'au seuil de Naurouze. Le bassin garantit un débit constant pour le canal, même en période de sécheresse.</>}
              </p>
              <p className="mt-4 text-fg/85 leading-relaxed italic">
                {en ? "It still operates today according to the principles defined by Riquet in the 17th century." : "Il fonctionne encore aujourd'hui selon les principes définis par Riquet au XVIIᵉ siècle."}
              </p>
            </motion.article>
          </div>

          <p className="mt-10 font-serif italic text-2xl md:text-3xl leading-tight max-w-4xl">
            {en ? "Without this duo — Saint-Ferréol storing, Naurouze sharing — the Canal du Midi could never have been born." : "Sans ce duo — Saint-Ferréol qui stocke, Naurouze qui partage — le Canal du Midi n'aurait jamais pu naître."}
          </p>
        </div>

        {/* Closing */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-24 border-t border-fg/20 pt-12"
        >
          <p className="font-serif text-3xl md:text-4xl italic leading-tight max-w-4xl tracking-tight">
            {en ? <>« To celebrate the 30th anniversary of the Canal du Midi's inscription as a UNESCO World Heritage Site, Les Arteurs present <span className="text-cinabre not-italic">Canal'Art 2026</span> across the canal's emblematic sites — in the footsteps of Riquet. »</> : <>« Pour célébrer le 30ᵉ anniversaire de l'inscription du Canal du Midi au patrimoine mondial de l'UNESCO, Les Arteurs organisent <span className="text-cinabre not-italic">Canal'Art 2026</span> sur les sites emblématiques du canal — dans les pas de Riquet. »</>}
          </p>

          {/* Lien vers le site institutionnel du Canal du Midi */}
          <div className="mt-10 border-l-2 border-cinabre pl-6 py-2 max-w-2xl" data-testid="riquet-canal-du-midi-link">
            <div className="meta-label text-cinabre mb-2">§ {en ? "To go further" : "Pour aller plus loin"}</div>
            <p className="text-fg/80 leading-relaxed">
              {en ? <>The full history, the structures, the governance and the life of the canal —{" "}</> : <>L'histoire complète, les ouvrages, la gouvernance et la vie du canal —{" "}</>}
              <a
                href="https://www.canal-du-midi.com"
                target="_blank"
                rel="noreferrer"
                className="text-cinabre underline decoration-cinabre/40 decoration-1 underline-offset-4 hover:decoration-cinabre transition-colors font-medium"
              >
                canal-du-midi.com
              </a>
              {en ? ", the official reference website." : ", le site institutionnel de référence."}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
